/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import * as d3 from 'd3';
import React, { useEffect, useRef } from 'react';
import {
  createScales,
  drawAxes,
  initializeSvg,
  plotHodograph,
} from './drawUtils';
import type { SoundingFeature } from '../../api/edr-api/types';

export interface HodographProps {
  data: SoundingFeature[][];
}

export const Hodograph: React.FC<HodographProps> = ({ data }) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const widthHeight = 400;

  useEffect(() => {
    if (!svgRef.current) {
      return;
    }

    // Define svg
    const svg = initializeSvg({
      svgRef,
      widthHeight,
    }) as unknown as d3.Selection<SVGSVGElement, unknown, HTMLElement, unknown>;

    // Set scales for temperature and pressure
    const [scaleRadius, scaleAngle] = createScales(widthHeight);

    // Add axis'
    drawAxes({
      svg,
      scaleRadius,
      scaleAngle,
      widthHeight,
    });

    data.forEach((features, index) => {
      const windValues = features?.map((feature) => {
        // Transform windDirectionFrom to windDirectionTo
        const windDirectionTo =
          (((feature.properties.windDirection + 180) % 360) + 360) % 360;

        return [windDirectionTo, feature.properties.windSpeed] as [
          number,
          number,
        ];
      });
      const pressureValues = features.map(
        (feature) => feature.properties.pressure,
      );

      // Draw Temperature
      plotHodograph({
        windValues,
        pressureValues,
        scaleAngle,
        scaleRadius,
        widthHeight,
        svg,
        index,
      });
    });

    // Allow zooming and panning
    const zoomBehavior = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, 7])
      .translateExtent([
        [-0.2 * widthHeight, -0.2 * widthHeight],
        [1.2 * widthHeight, 1.2 * widthHeight],
      ])
      .on('zoom', (event) => {
        svg.select('g.hodograph').attr('transform', event.transform);
      });
    svg.call(zoomBehavior);

    // eslint-disable-next-line consistent-return
    return (): void => {
      svg.on('zoom', null);
      svg.select('g.hodograph').selectAll('*').remove();
    };
  }, [data, widthHeight]);

  return <svg height="100%" ref={svgRef} width="100%" />;
};
